import { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, Marker, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { getGeoapifyKey, getGeoapifyTileUrl, OSM_ATTRIBUTION, PINK_COLOR, decodePolyline } from "@/lib/osm";
import { Loader2, Navigation } from "lucide-react";
import type { Shop, Route } from "@shared/schema";
import { useI18n } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { useRoutingConfig } from "@/hooks/use-routing-config";


// Fix for default marker icons in Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
    iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
    shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

interface OSMRouteMapProps {
    route?: Route;
    shops: Shop[];
    polyline?: string;
    startAddress?: { lat?: number, lng?: number, address?: string } | null;
    endAddress?: { lat?: number, lng?: number, address?: string } | null;
    extraStop?: { lat?: number, lng?: number, address?: string } | null;
    height?: string;
    className?: string;
    onRouteCalculated?: (metrics: { distance: string; duration: string; polyline?: string }) => void;
    onShopClick?: (shopId: string, index: number) => void;
}

/**
 * Helper component to fit map bounds to route
 */
function ChangeView({ bounds }: { bounds: L.LatLngBoundsExpression | null }) {
    const map = useMap();
    useEffect(() => {
        if (bounds) {
            map.fitBounds(bounds, { padding: [50, 50] });
        }
    }, [bounds, map]);
    return null;
}

export function OSMRouteMap({
    route,
    shops,
    polyline,
    startAddress,
    endAddress,
    extraStop,
    height = "400px",
    className = "",
    onRouteCalculated,
    onShopClick
}: OSMRouteMapProps) {
    const { t } = useI18n();
    const [internalRouteData, setInternalRouteData] = useState<any>(null);
    const land = (route as any)?.land || (route as any)?.country || "nl";
    const { data: config } = useRoutingConfig(land);
    const [loading, setLoading] = useState(false);


    // Get polyline from props, route model or internal fetch
    const activePolyline = polyline || (route as any)?.osm_route_data?.polyline || (route as any)?.google_route_data?.polyline || internalRouteData?.polyline;

    // Auto-calculate if no polyline and we have enough stops
    useEffect(() => {
        const autoCalculate = async () => {
            if (activePolyline || shops.length < 2 || loading) return;

            setLoading(true);
            // Also wipe internal state so it doesn't cache the old polyline internally forever
            // if the parent resets routeMetrics
            try {
                // Combine all points for routing
                const allPoints: [number, number][] = [];
                if (startAddress?.lat && startAddress?.lng) allPoints.push([startAddress.lng, startAddress.lat]);
                shops.forEach(s => { if (s.lat && s.lng) allPoints.push([s.lng, s.lat]); });
                if (extraStop?.lat && extraStop?.lng) allPoints.push([extraStop.lng, extraStop.lat]);
                if (endAddress?.lat && endAddress?.lng) allPoints.push([endAddress.lng, endAddress.lat]);

                if (allPoints.length < 2) return;

                const land = (route as any)?.land || (route as any)?.country || "nl";
                const response = await fetch("/api/osm/route", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ coordinates: allPoints, country: land })
                });

                if (response.ok) {
                    const data = await response.json();
                    setInternalRouteData(data);

                    if (onRouteCalculated) {
                        onRouteCalculated({
                            polyline: data.polyline,
                            distance: (data.distance_meters / 1000).toFixed(1) + " km",
                            duration: Math.round(data.duration_seconds / 60) + " min"
                        });
                    }
                }
            } catch (e) {
                console.error("[OSMRouteMap] Auto-calculate failed:", e);
            } finally {
                setLoading(false);
            }
        };

        autoCalculate();
    }, [activePolyline, shops, startAddress, endAddress, extraStop, loading, onRouteCalculated, route]);

    // Track shop order changes to clear internal polyline if they changed but no new activePolyline was provided
    useEffect(() => {
        setInternalRouteData(null);
    }, [shops, startAddress, endAddress, extraStop]);

    const polylineCoords = useMemo(() => {
        if (!activePolyline) return [];
        return decodePolyline(activePolyline).map(([lat, lng]) => [lat, lng] as [number, number]);
    }, [activePolyline]);

    const bounds = useMemo(() => {
        const allCoords: [number, number][] = [...polylineCoords];

        // Add markers to bounds if no polyline
        if (allCoords.length === 0) {
            shops.forEach(s => { if (s.lat && s.lng) allCoords.push([s.lat, s.lng]); });
            if (startAddress?.lat && startAddress?.lng) allCoords.push([startAddress.lat, startAddress.lng]);
            if (endAddress?.lat && endAddress?.lng) allCoords.push([endAddress.lat, endAddress.lng]);
            if (extraStop?.lat && extraStop?.lng) allCoords.push([extraStop.lat, extraStop.lng]);
        }

        if (allCoords.length === 0) return null;
        return L.latLngBounds(allCoords);
    }, [polylineCoords, shops, startAddress, endAddress, extraStop]);

    const createIcon = (label: string, color: string = PINK_COLOR) => {
        return L.divIcon({
            className: "custom-div-icon",
            html: `
        <div style="
          background-color: ${color};
          color: white;
          width: 24px;
          height: 24px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: bold;
          font-size: 11px;
          border: 2px solid white;
          box-shadow: 0 2px 4px rgba(0,0,0,0.3);
        ">
          ${label}
        </div>
      `,
            iconSize: [24, 24],
            iconAnchor: [12, 12],
        });
    };

    // 🔑 KEY CHECK - DEV only
    useEffect(() => {
        if (import.meta.env.DEV) {
            const key = getGeoapifyKey();
            console.log('[OSMRouteMap] Geoapify key:', key ? '✅ loaded' : '⚠️ missing → plain OSM fallback');
        }
    }, [config]);

    return (
        <div className={`relative w-full rounded-lg overflow-hidden border ${className}`} style={{ height }}>
            {loading && (
                <div className="absolute inset-0 z-[1000] bg-background/50 backdrop-blur-sm flex items-center justify-center">
                    <Loader2 className="h-8 w-8 text-primary animate-spin" />
                </div>
            )}

            <MapContainer
                center={[52.1326, 5.2913]}
                zoom={7}
                style={{ height: "100%", width: "100%" }}
                scrollWheelZoom={false}
            >
                {/* TILELAYER — Geoapify for routes, plain OSM as fallback */}
                <TileLayer
                    attribution={OSM_ATTRIBUTION}
                    url={getGeoapifyTileUrl()}
                />
                <ChangeView bounds={bounds} />

                {polylineCoords.length > 0 && (
                    <Polyline
                        positions={polylineCoords}
                        color={PINK_COLOR}
                        weight={4.0}
                        opacity={0.85}
                    />
                )}

                {/* Start Marker */}
                {startAddress?.lat && startAddress?.lng && (
                    <Marker
                        position={[startAddress.lat, startAddress.lng]}
                        icon={createIcon("S", "#000")}
                    />
                )}

                {/* Shop Markers */}
                {shops.map((shop, idx) => {
                    if (!shop.lat || !shop.lng) return null;
                    return (
                        <Marker
                            key={`shop-${shop.id}`}
                            position={[shop.lat, shop.lng]}
                            icon={createIcon((idx + 1).toString())}
                            eventHandlers={{
                                click: () => onShopClick?.(shop.id, idx)
                            }}
                        />
                    );
                })}

                {/* Extra Stop Marker */}
                {extraStop?.lat && extraStop?.lng && (
                    <Marker
                        position={[extraStop.lat, extraStop.lng]}
                        icon={createIcon("+", "#444")}
                    />
                )}

                {/* End Marker */}
                {endAddress?.lat && endAddress?.lng && (
                    <Marker
                        position={[endAddress.lat, endAddress.lng]}
                        icon={createIcon("E", "#000")}
                    />
                )}
            </MapContainer>
        </div>
    );
}
